import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { demoMode } from "../lib/config";
import { SupportEmailFallback, SupportLink } from "./support-link";

/** Loading state — shown while an async resource is in flight. */
export function LoadingState({ label = "Loading…" }: { label?: string }): JSX.Element {
  return (
    <div className="state" aria-busy="true">
      <div className="spinner" aria-hidden="true" />
      <p className="state__title">{label}</p>
    </div>
  );
}

/** Empty state — a resource loaded but has nothing to show yet. */
export function EmptyState({
  title,
  children,
}: {
  title: string;
  children?: ReactNode;
}): JSX.Element {
  return (
    <div className="state">
      <p className="state__title">{title}</p>
      {children}
    </div>
  );
}

/**
 * API-unavailable state — the control API could not be reached.
 *
 * In demo mode (`PUBLIC_DEMO_MODE=true`) this is expected: the public demo isn't
 * wired to a live control API, so we show a calm, clearly-labelled demo notice
 * instead of an alarming error. This makes every screen read as "you're viewing a
 * demo" rather than "the app is broken".
 */
export function ApiUnavailableState({ detail }: { detail?: string }): JSX.Element {
  if (demoMode) {
    return (
      <div className="state state--demo">
        <span className="state__demoTag">Demo</span>
        <p className="state__title">You’re viewing a demo of ODIN</p>
        <p>
          This is a preview of the ODIN dashboard — it isn’t connected to a live control API or a
          real Discord server, so the panels below stay empty on purpose. Nothing here touches a
          real server.
        </p>
        <p>
          <Link to="/login" className="ax-card__link">
            Start free
          </Link>{" "}
          to connect your Discord server and see live data.
        </p>
      </div>
    );
  }
  return (
    <div className="state" role="alert">
      <p className="state__title">Can’t reach the ODIN control API</p>
      <p>
        The dashboard is loaded, but it couldn’t reach the API.{" "}
        {detail ?? "It may be starting up, or the API URL isn’t configured yet."}
      </p>
      {/* The one place in the signed-in product that still offers an email address,
          and it does so because the alternative would be dishonest: the support desk
          is served by the very API this state is reporting as unreachable, so a link
          to it would lead to a page that also cannot load. The wording says that
          plainly rather than sending the customer to a dead end. See
          docs/support/SUPPORT_EMAIL_RETENTION.md. */}
      <SupportEmailFallback reason="Opening a support conversation needs this same API, so if it keeps failing, email" />
    </div>
  );
}

/**
 * Honest "not configured" state — a feature is intentionally unavailable in this
 * phase. We show this instead of fabricating data.
 */
export function NotConfiguredState({
  title,
  children,
}: {
  title: string;
  children?: ReactNode;
}): JSX.Element {
  return (
    <div className="state">
      <p className="state__title">{title}</p>
      <p>{children ?? "This will become available in a later phase."}</p>
      {/* Unlike the API-unavailable state, nothing is broken here — the API is
          reachable, the feature simply is not on yet. So the support desk works, and
          it is the right place to ask when this will change. */}
      <p className="state__support">
        <SupportLink>Contact ODIN Support</SupportLink> if you need this sooner.
      </p>
    </div>
  );
}
